import React, { useState } from 'react';
import { NewProjectUserFormState } from 'src/app/admin/views/user-management/state/userManagementSlice';
import Checkbox from 'src/shared/components/checkbox/Checkbox';
import { getRoleLabel } from 'src/app/admin/views/user-management/components/userManagementHelpers';
import { useAppSelector } from 'src/store';
import { Role } from 'src/shared/types';
import styles from './roleCheckbox.module.scss';
import { getUserManagementGeneralUiState } from '../../state/userManagementSelector';

type CustomerRoleCheckboxesProps = {
    roles: Role[];
    defaultRoles?: Role[] | null;
    /** Whether the user being created/edited is internal — the caller's job, since "new user" and
     * "user being edited" are tracked in separate Redux slices and this component must not guess. */
    isInternalUser: boolean;
    onChange: (fragment: Partial<NewProjectUserFormState['formState']>) => void;
};

/**
 * CUSTOMER_ADMIN / CUSTOMER_VIEWER checkboxes — real, backend-persisted Roles, external-user-only.
 * Renders nothing for an internal user. See OrganisationalViewerCheckbox for the Cognito-group
 * checkbox this used to be bundled with.
 */
const CustomerRoleCheckboxes = (props: CustomerRoleCheckboxesProps) => {
    const { roles, onChange, defaultRoles = [], isInternalUser } = props;
    const [checkedRoles, setCheckedRoles] = useState<Role[] | null>(defaultRoles);
    const generalUiState = useAppSelector(getUserManagementGeneralUiState);
    const { hasCustomerAdminInUsers } = generalUiState;

    const handleCheckboxChange = (isChecked: boolean, roleName: string) => {
        const currentRoles = Array.isArray(roles) ? roles : [];
        const updatedRoles = isChecked ? [...currentRoles, roleName] : currentRoles.filter((role: string) => role !== roleName);
        onChange({ roles: updatedRoles as Role[] });
        setCheckedRoles(updatedRoles as Role[]);
    };

    if (isInternalUser) {
        return null;
    }

    return (
        <div data-testid='user-management-assign-admin-roles' className={styles.container}>
            <div className={styles.roleOption}>
                <Checkbox
                    label={getRoleLabel(Role.CUSTOMER_ADMIN)}
                    onChange={value => handleCheckboxChange(value, Role.CUSTOMER_ADMIN)}
                    checked={Boolean(checkedRoles?.includes(Role.CUSTOMER_ADMIN))}
                    disabled={hasCustomerAdminInUsers}
                />
                <p className={styles.roleDescription}>Can manage all of the projects for this customer, and invite and manage users.</p>
            </div>
            <div className={styles.roleOption}>
                <Checkbox
                    label={getRoleLabel(Role.CUSTOMER_VIEWER)}
                    onChange={value => handleCheckboxChange(value, Role.CUSTOMER_VIEWER)}
                    checked={Boolean(checkedRoles?.includes(Role.CUSTOMER_VIEWER))}
                />
                <p className={styles.roleDescription}>
                    Doesn&apos;t have permission to manage users or projects, but can view the GNR product.
                </p>
            </div>
        </div>
    );
};

export default CustomerRoleCheckboxes;
